import { createFileRoute, Link } from '@tanstack/react-router';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { AuthCard } from '@sentinel/ui';
import { useVerifyEmail, type VerifyAction } from '../features/verify-email/use-verify';

const searchSchema = z.object({
  action: z.enum(['email_change']).optional(),
});

type ErrorKind = 'expired' | 'already_used' | 'invalid';
type ViewState =
  | { kind: 'pending' }
  | { kind: 'success' }
  | { kind: 'error'; reason: ErrorKind };

interface ErrorBody {
  error?: string;
  message?: string;
}

function getStatus(e: unknown): number | undefined {
  return (e as { status?: number } | null)?.status;
}

function getBody(e: unknown): ErrorBody | undefined {
  if (e && typeof e === 'object') {
    const maybe = e as { body?: ErrorBody } & ErrorBody;
    return maybe.body ?? maybe;
  }
  return undefined;
}

export function classifyVerifyError(_status: number | undefined, message: string | undefined): ErrorKind {
  const m = (message ?? '').toLowerCase();
  if (m.includes('expired')) return 'expired';
  if (m.includes('already')) return 'already_used';
  return 'invalid';
}

function VerifyEmailPage() {
  const { t } = useTranslation('verify-email');
  const { token } = Route.useParams();
  const { action: actionParam } = Route.useSearch();
  const action: VerifyAction = actionParam === 'email_change' ? 'email_change' : 'verify';
  const verify = useVerifyEmail(action);
  const [view, setView] = useState<ViewState>({ kind: 'pending' });
  const fired = useRef(false);

  // Strip the token from history so a Back button / refresh cannot replay
  // the (now-consumed) credential.
  useEffect(() => {
    if (typeof window === 'undefined' || !window.history.replaceState) return;
    const cleanPath = window.location.pathname.replace(/\/[^/]+$/, '/_consumed');
    window.history.replaceState(null, '', cleanPath + window.location.search);
  }, []);

  useEffect(() => {
    if (fired.current) return;
    fired.current = true;
    void (async () => {
      try {
        await verify.mutateAsync(token);
        setView({ kind: 'success' });
      } catch (e) {
        const status = getStatus(e);
        const body = getBody(e);
        const reason = classifyVerifyError(status, body?.message);
        setView({ kind: 'error', reason });
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return <AuthCard>{renderState(view, action, t)}</AuthCard>;
}

function renderState(
  view: ViewState,
  action: VerifyAction,
  t: ReturnType<typeof useTranslation>['t'],
) {
  if (view.kind === 'pending') {
    return (
      <>
        <h1>{t('title_pending')}</h1>
        <p role="status" aria-live="polite">{t('message_pending')}</p>
      </>
    );
  }
  if (view.kind === 'success') {
    if (action === 'email_change') {
      return (
        <>
          <h1>{t('title_email_changed')}</h1>
          <p>{t('message_email_changed')}</p>
          <p>
            <Link to="/login">{t('continue_to_login')}</Link>
          </p>
        </>
      );
    }
    return (
      <>
        <h1>{t('title_success')}</h1>
        <p>{t('message_success')}</p>
        <p>
          <Link to="/login">{t('continue_to_login')}</Link>
        </p>
      </>
    );
  }
  // error
  const { reason } = view;
  if (reason === 'expired') {
    return (
      <>
        <h1>{t('title_expired')}</h1>
        <p>{t('message_expired')}</p>
        <p>
          <Link to="/welcome">{t('request_new_link')}</Link>
        </p>
      </>
    );
  }
  if (reason === 'already_used') {
    return (
      <>
        <h1>{t('title_already_used')}</h1>
        <p>{t('message_already_used')}</p>
        <p>
          <Link to="/login">{t('continue_to_login')}</Link>
        </p>
      </>
    );
  }
  return (
    <>
      <h1>{t('title_invalid')}</h1>
      <p>{t('message_invalid')}</p>
      <p>
        <Link to="/login">{t('continue_to_login')}</Link>
      </p>
    </>
  );
}

export const Route = createFileRoute('/verify-email/$token')({
  validateSearch: searchSchema,
  component: VerifyEmailPage,
});
